// The sandbox dashboard's half of a sandbox sign-in from production (DSEC D3; backend plan
// DEVELOPER-SECTION-PLAN v2 §1). Contract: apiv2/dashboard_auth.openapi.yaml dashboardSandboxHandoff --
// POST /v2/dashboard_auth/sandbox_handoff/ {code, host} → 201 a session (+ landing) or {code, detail}.
//
// Production's "Open sandbox" (developer.openapi.yaml developerSandboxSignin) answers
// `https://<sandbox>.dashboard…/auth/sandbox-handoff#code=<one-time code>`. The code rides in the fragment, which
// the browser never sends anywhere; this page takes it out of the address bar before anything else (history,
// screenshots, a reload) and exchanges it once.
import { DASHBOARD_AUTH_BASE } from './dashboardAuth';
import { pagePath } from '../developer/routes';

export const SANDBOX_HANDOFF_PATH = '/auth/sandbox-handoff';

const CODE_RE = /^[A-Za-z0-9_-]{16,128}$/;

/** The `code` of the fragment (`#code=…`), removed from the address bar (path and query kept). Null when there is
 *  none or it is not a code; the fragment is dropped either way. */
export function takeCodeFromHash(location: Location = window.location, history: History = window.history): string | null {
    const hash = location.hash || '';
    if (!hash) { return null; }
    try {
        history.replaceState(history.state, '', `${location.pathname}${location.search}`);
    } catch (_e) {
        // The exchange still happens; the code is single use anyway.
    }
    const code = new URLSearchParams(hash.replace(/^#/, '')).get('code');
    return code && CODE_RE.test(code) ? code : null;
}

// The code moved out of the URL before Sentry.init (sentrySetup.js): kept in this module's memory only, never in
// storage. `undefined`: nothing was stashed (the page then reads the fragment itself).
let stashedCode: string | null | undefined;

/** Before Sentry starts (sentrySetup.js): on this page, move the fragment's code into memory, so the SDK's history
 *  instrumentation never records a URL with it (the signup host's rule). Elsewhere nothing. */
export function stashHandoffCode(location: Location = window.location, history: History = window.history): void {
    if (location.pathname !== SANDBOX_HANDOFF_PATH) { return; }
    stashedCode = takeCodeFromHash(location, history);
}

/** The code, once: the stashed one, else the fragment's (removed from the address bar). */
export function takeHandoffCode(): string | null {
    if (stashedCode !== undefined) {
        const code = stashedCode;
        stashedCode = null;
        return code;
    }
    return takeCodeFromHash();
}

export interface HandoffAnswer {
    status: number;
    body: any;
}

/** POST the code with this page's own host (never `config.domainOverride`: the backend checks that the page the
 *  code landed on is the sandbox's). Resolves {status, body}; rejects only on a network error. */
export async function postSandboxHandoff(code: string, host: string = window.location.host): Promise<HandoffAnswer> {
    const res = await fetch(`${DASHBOARD_AUTH_BASE}/sandbox_handoff/`, {
        method: 'POST',
        cache: 'no-store',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, host }),
    });
    const body = await res.json().catch(() => null);
    return { status: res.status, body };
}

const AGAIN = ' Go back to your production dashboard and open the sandbox again.';

// Words for every DashboardSandboxHandoffErrorCode (sandboxHandoff.test.ts holds the list to the contract), and the
// page's own codes (CLIENT_FAILURES).
export const HANDOFF_FAILURE_TEXT: Record<string, string> = {
    invalid_request: `This sign-in link is incomplete.${AGAIN}`,
    handoff_invalid: `This sign-in link is invalid, already used or expired (it works once, for a minute).${AGAIN}`,
    sandbox_suspended: 'The sandbox is suspended. Resume it from your production dashboard (Developer → Overview), then open it again.',
    target_not_found: `That sandbox user can no longer sign in.${AGAIN}`,
    throttled: 'Too many sign-in attempts. Wait a few minutes, then open the sandbox again from production.',
    signin_unavailable: `The sign-in could not be recorded, so it was not made.${AGAIN}`,
    // The page's own:
    missing_code: `This sign-in link has no code.${AGAIN}`,
    unreachable: 'The server could not be reached. Check your connection, then open the sandbox again from production.',
};

export const CLIENT_FAILURES = ['missing_code', 'unreachable'];

/** What to tell the user about a failed exchange: the code's words, else the server's, else a generic line. */
export function describeHandoffFailure(answer: HandoffAnswer | null): string {
    const code = answer?.body && typeof answer.body.code === 'string' ? answer.body.code : null;
    if (code && HANDOFF_FAILURE_TEXT[code]) { return HANDOFF_FAILURE_TEXT[code]; }
    if (answer?.body && typeof answer.body.detail === 'string' && answer.body.detail) { return answer.body.detail; }
    return `The sandbox sign-in did not work.${AGAIN}`;
}

/** The sandbox page to open after the exchange (a Developer page key; the Overview when unknown). */
export const landingPath = (landing: unknown) => pagePath(typeof landing === 'string' ? landing : 'overview');

/** Production's side: follow only an absolute https URL (http where allowHttp: local development) to this page on
 *  another dashboard -- never a `javascript:` URL, a relative path or garbage from a broken answer. */
export function sandboxHandoffTarget(raw: unknown, { allowHttp = false }: { allowHttp?: boolean } = {}): string | null {
    if (typeof raw !== 'string') { return null; }
    let url: URL;
    try {
        url = new URL(raw);
    } catch (_e) {
        return null;
    }
    if (!(url.protocol === 'https:' || (allowHttp && url.protocol === 'http:'))) { return null; }
    if (url.pathname !== SANDBOX_HANDOFF_PATH || !url.hash.startsWith('#code=')) { return null; }
    return url.toString();
}
